import { configureStore, combineReducers } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // Default is localStorage
import authReducer from './slices/authSlice';
import themeReducer from './slices/themeSlice';
import resourceReducer from './slices/resourceSlice';
import customerReducer from './slices/customerSlice';
import serviceReducer from './slices/serviceSlice';
import serviceResourceMappingReducer from './slices/serviceResourceMappingSlice';
import bookingReducer from './slices/bookingSlice';
import organizationReducer from './slices/organizationSlice';
import organizationContextReducer from './slices/organizationContextSlice';
import calendarReducer from './slices/calendarSlice';
import workingHoursReducer from './slices/workingHoursSlice';
import chatLogsReducer from './slices/chatHistorySlice';
import chatBookingReducer from './slices/chatBookingSlice';
import dashboardReducer from './slices/dashboardSlice';
import smsSettingsReducer from './slices/smsSettingsSlice';
import emailSettingsReducer from './slices/emailSettingsSlice';
import stripeReducer from './slices/stripeSlice';
import unavailableReducer from './slices/unavailableDatesSlice';
import filesReducer from './slices/filesSlice';
import formReducer from './slices/formSlice';
import paymentReducer from './slices/paymentSlice';
import phoneNumbersReducer from './slices/phoneNumbersSlice';
import callRoutingReducer from './slices/callRoutingSlice';

// Combine reducers
const rootReducer = combineReducers({
  auth: authReducer,
  theme: themeReducer,
  resources: resourceReducer,
  customers: customerReducer,
  services: serviceReducer,
  serviceResourceMappings: serviceResourceMappingReducer,
  bookings: bookingReducer,
  chatLogs: chatLogsReducer,
  chatBookingLogs: chatBookingReducer,
  organizations: organizationReducer,
  organizationContext: organizationContextReducer,
  calendar: calendarReducer,
  workingHours: workingHoursReducer,
  dashboard: dashboardReducer,
  smsSettings: smsSettingsReducer,
  emailSettings: emailSettingsReducer,
  stripe: stripeReducer,
  unavailableDates: unavailableReducer,
  files: filesReducer,
  forms: formReducer,
  payment: paymentReducer,
  phoneNumbers: phoneNumbersReducer,
  callRouting: callRoutingReducer,
});

// Persist configuration
const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['theme'], // Example: exclude the theme slice
};

// Persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
  actionTransformer: action => action,
  attachReduxState: false,
  stateTransformer: state => state,
});

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  enhancers: getDefaultEnhancers => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),

  devTools: import.meta.env.VITE_AWS_ENV !== 'prod',
});

// Create persistor
export const persistor = persistStore(store);

// Type definitions
export type RootState = ReturnType<typeof rootReducer>; // Updated to use combined reducer
export type AppDispatch = typeof store.dispatch;
